import type { PetitionDetailsPageUserData } from '#src/app/pages/petitionDetails/pageContext';
import { usePetitionDetailsPageData, usePetitionDetailsPageUserData } from '#src/app/pages/petitionDetails/pageContext';

type SpikingMobileExperiment = PetitionDetailsPageUserData['experiments']['petitionPageSpikingMobileExperiment'];

type Result = Readonly<{
	experiment: SpikingMobileExperiment;
	isRelevant: boolean;
	isVariantOn: boolean;
	modules: Readonly<{ momentumCard: boolean; signerTicker: boolean }>;
}>;

type RelevanceInput = Readonly<{
	isListed: boolean;
	isMomentumCardOn: boolean;
	dailySignatureCount: number;
	status: string;
	decisionMaker: boolean;
	signed: boolean;
	ownPetition: boolean;
	hasVideoFirstHero: boolean;
}>;

/**
 * The FCM lists the petitions the experiment runs on. Every other case keeps today's page and is left
 * out of the readout in both arms: the card switched off or with nothing to show (no signatures in
 * the last 24 hours), closed/victorious petitions, decision makers, viewers who already signed,
 * starters viewing their own petition, and petitions using the video-first hero (whose docked sign
 * button leaves no inline button for the card to sit above).
 */
function isRelevantPetition(input: RelevanceInput): boolean {
	if (!input.isListed || !input.isMomentumCardOn || input.dailySignatureCount === 0) return false;
	if (input.status === 'CLOSED' || input.status === 'VICTORY') return false;
	return !input.decisionMaker && !input.signed && !input.ownPetition && !input.hasVideoFirstHero;
}

/**
 * Resolves whether the spiking mobile treatment applies to this petition and viewer. When the variant
 * is on, the momentum card renders and replaces today's mobile momentum bar.
 *
 * EXPERIMENT: petition_page_spiking_mobile_experiment — remove with experiment cleanup
 */
export function useSpikingMobileExperiment(): Result {
	const {
		data: {
			petition: { id: petitionId, status, dailySignatureCount },
			featureConfigs: { petitionPageSpikingVariant: fcm },
			videoFirstHero,
		},
	} = usePetitionDetailsPageData();
	const {
		data: {
			decisionMaker,
			signed,
			ownPetition,
			experiments: { petitionPageSpikingMobileExperiment: experiment },
		},
	} = usePetitionDetailsPageUserData();

	const modules = {
		momentumCard: fcm?.modules.momentum_card ?? false,
		signerTicker: fcm?.modules.signer_ticker ?? false,
	};
	const isRelevant = isRelevantPetition({
		isListed: fcm?.petition_ids.includes(Number(petitionId)) ?? false,
		isMomentumCardOn: modules.momentumCard,
		dailySignatureCount,
		status,
		decisionMaker,
		signed,
		ownPetition,
		hasVideoFirstHero: videoFirstHero.enabled,
	});
	const isVariantOn = isRelevant && experiment.variation === 'variation_1';

	return { experiment, isRelevant, isVariantOn, modules };
}
